import { noteApiFailure } from "../app/diagnostics";
/**
 * THE REST HALF OF THE SERVER CONTRACT. One owner.
 *
 * ⭐ **EVERY ROUTE THE CLIENT USES IS DECLARED HERE, AND NOWHERE ELSE.** The previous client reached
 * the backend from wherever it happened to need it, which is why "which routes does the client
 * actually depend on" took a tool (`tools/verify_route_shapes.py`) to answer. Here it is a file you
 * can read.
 *
 * 🚨 **A LIST-TYPED ROUTE MUST ANSWER WITH AN ARRAY, AND `{}` IS MALFORMED RATHER THAN EMPTY.**
 * Measured 2026-08-13: six routes answered `{}` for a list and it is what made PLAY dead. The old
 * client deserialised into typed C# models and threw; this one is TypeScript and would silently
 * carry `undefined` into a `.map()`, which is worse. `list()` therefore normalises and SAYS SO -
 * a shape violation is logged, never quietly absorbed.
 *
 * ⚠️ **THE TOKEN IS HELD IN MEMORY AND MIRRORED TO sessionStorage, NOT localStorage.** The old
 * client persisted a JWT and re-sent a stale one after the server had moved on, which presents as
 * an unexplained kick to login. Session scope dies with the tab, which is the correct lifetime for
 * a closed-beta guest identity.
 */

export interface LoginUser {
  id: string;
  accessToken: string;
  refreshToken: string;
  userName: string;
  email: string | null;
}

const TOKEN_KEY = "revival.accessToken";

let token: string | null = sessionStorage.getItem(TOKEN_KEY);

export function setToken(t: string | null): void {
  token = t;
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
  else sessionStorage.removeItem(TOKEN_KEY);
}

export function getToken(): string | null {
  return token;
}

/**
 * ⚠️ **`credentials: "include"` IS LOAD-BEARING.** The closed beta is an HMAC-signed cookie
 * (`WalletGate`), and the identity `LoginGuest` returns is DERIVED FROM THAT COOKIE, never from
 * anything we send. Omit it and every request is an anonymous one that the server correctly refuses,
 * which reads on screen as "the game will not load".
 */
async function request<T>(method: "GET" | "POST" | "PATCH" | "DELETE", path: string,
                          body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  // ⚠️ `body` is spread in only when there IS one. Under `exactOptionalPropertyTypes` an explicit
  // `body: undefined` is not the same as an absent key, and the distinction is real at runtime too:
  // a GET with a body key set is not a request every proxy handles identically.
  const init: RequestInit = { method, headers, credentials: "include" };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(path, init);

  if (!res.ok) {
    // ⛔ THE SERVER'S OWN WORDS, NOT A GENERIC MESSAGE. Its refusals are written for a player
    // (`ClosedBeta` names the one door that is actually open); replacing them with "request failed"
    // throws away the only useful half.
    const text = await res.text().catch(() => "");
    // ⭐ RECORDED BEFORE IT IS THROWN, and recorded whether or not anyone catches it. Several calls
    // here are deliberately non-fatal (`void Api.userData().catch(...)` at boot), so a failure that
    // is only visible to whoever catches it is invisible to the bug reporter - which is exactly the
    // hole found on 2026-08-17: a 400 on LoginGuest produced a report with an empty error list.
    noteApiFailure(method, path, res.status, text);
    throw new ApiError(res.status, path, text);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly path: string, readonly body: string) {
    super(`${status} ${path}${body ? ` - ${body.slice(0, 300)}` : ""}`);
    this.name = "ApiError";
  }
}

/**
 * A route the client expects to be a LIST. Returns [] for a malformed answer and logs it loudly.
 *
 * ⭐ This is the `{}`-for-a-list defect made harmless AND visible at the same time. Swallowing it
 * silently would reproduce the original bug with a nicer failure mode; refusing outright would make
 * one bad route take down a screen that could otherwise render.
 */
async function list<T>(path: string): Promise<T[]> {
  const v = await request<unknown>("GET", path);
  if (Array.isArray(v)) return v as T[];
  console.error(
    `[api] SHAPE VIOLATION: ${path} is declared list-typed and answered ` +
      `${JSON.stringify(v)?.slice(0, 120)}. Treating as empty. '{}' for a list is MALFORMED, ` +
      `not empty - fix the server route, not this call site.`,
  );
  return [];
}

export const Api = {
  /** Is the beta gate satisfied for this browser? Answers before anything else is attempted. */
  gateMe: () => request<{ address: string | null }>("GET", "/api/gate/me"),

  /**
   * ⚠️ deviceId "n/a" IS A REAL VALUE THE OLD CLIENT SENT, and it collapsed every browser tester
   * onto one shared save (`connected user=beta-n/a`). The server now mints a fresh identity for a
   * useless id, so sending a real one per browser is still the right thing to do.
   */
  loginGuest: (deviceId: string) =>
    request<{ user: LoginUser }>("POST", "/api/v1/Identity/LoginGuest", { deviceId, client: "web" }),

  userData: () => request<Record<string, unknown>>("GET", "/api/v1/Identity/UserData"),
  activeSession: () => request<Record<string, unknown>>("GET", "/api/v1/Lobby/ActiveSession"),

  /**
   * Leave whatever match the server thinks I am in. **Over HTTP, on purpose.**
   *
   * ⛔ Every other exit rides the SignalR hub (the CONCEDE button sends `GameEndCmd`), so when the
   * hub is the thing that failed there was NO way out but to wait 30 minutes for the idle reaper -
   * while `activeSession()` kept handing the client the same dead match to auto-rejoin. That is a
   * loop, and a real player reported it: *"i am stuck in a loop of this dule with a bot. I can not
   * make or enter a new Duel."*
   *
   * ⚠️ Abandons, does not concede. No result is recorded either way.
   */
  abandonSession: () => request<{ ok: boolean; abandoned: boolean }>("POST", "/api/v1/Lobby/AbandonSession"),

  /** The card and hero database the whole client renders from. */
  gameDatabase: () => request<Record<string, unknown>>("GET", "/api/v1/Game/GameDatabaseModel"),
  gameConfig: () => request<Record<string, unknown>>("GET", "/api/v1/Game/GameConfig"),

  leagues: () => list<{ id: string; name?: string }>("/api/v1/Lobby/AvailableLeagues"),
  decks: () => list<{ id: string; name?: string; heroId?: string }>("/api/v1/Deck"),
  selectDeck: (deckId: string) => request<unknown>("POST", "/api/v1/Deck/Select", { deckId }),

  /**
   * Save a deck.
   *
   * ⚠️ **PATCH MERGES, AND `ownedCardIds` IS THE ONE ARRAY IT REPLACES.** The server merges the
   * fields you send and leaves the rest alone - deliberately, because `commanderOwnedIronsideId` is
   * set elsewhere and a wholesale replace would drop it ("no portrait, no name"). So send only what
   * the builder actually edits.
   *
   * ⚠️ **`ownedCardIds` ARE OWNED-CARD ENTITY IDS, NOT CARD IDS.** Two copies of the same card are
   * two different owned entities. Sending card ids would save a deck the server cannot resolve.
   */
  /**
   * Create a NEW deck. POST, never PATCH.
   *
   * ⚠️ The server mints the id and refuses past `maxDeckCount` with a message - show it. A create
   * that silently overwrote an existing deck would be the worst possible surprise, which is why the
   * server ignores any id sent here.
   */
  createDeck: (deck: { name: string; heroId: string; ownedCardIds: string[] }) =>
    request<Record<string, unknown>>("POST", "/api/v1/Deck", deck),

  /**
   * Delete a deck.
   *
   * ⚠️ The server refuses to delete your LAST deck, with a message - deleting your way into an
   * account that cannot enter a match is a dead end by another door. Show what it says.
   */
  deleteDeck: (id: string) => request<unknown>("DELETE", "/api/v1/Deck", { id }),

  saveDeck: (deck: { id?: string; name?: string; heroId?: string; ownedCardIds: string[] }) =>
    request<Record<string, unknown>>("PATCH", "/api/v1/Deck", deck),

  /**
   * Start a campaign stage.
   *
   * ⚠️ The stage's advertised handicaps are resolved SERVER-SIDE from (quadrant, stageIndex,
   * heroic) - the client must never send modifiers, or the promise and the match could disagree.
   */
  campaignStartStage: (o: { quadrant: string; stageIndex: number; isHeroic: boolean; deckId?: string }) =>
    request<Record<string, unknown>>("POST", "/api/v1/Campaign/StartStage", o),

  campaignProgress: () => request<Record<string, unknown>>("GET", "/api/v1/Campaign/Progress"),

  /**
   * The player's award receipts, newest first.
   *
   * ⭐ Exists because an award used to be a pure SIDE EFFECT: cards and RXP landed in the player's
   * documents and nothing ever told them. Alex, after winning twice: *"I have no idea what I won."*
   *
   * ⚠️ Returns card IDS, not names - `match/cards.ts` owns naming, so a de-brand rename needs no
   * release here.
   */
  /**
   * Choose your own display name. The server owns the length and character rules and states them
   * in its own words - do not restate a limit here, it was wrong for weeks in exactly that way.
   */
  setUserName: (userName: string) =>
    request<{ userName: string }>("POST", "/api/v1/Identity/SetUserName", { userName }),

  /** A new themed name, drawn from the same generator that names a player who has not chosen. */
  rerollName: () =>
    request<{ userName: string }>("POST", "/api/v1/Identity/RerollName", {}),

  /** A nonce plus the exact text the wallet will be asked to sign. */
  gateNonce: () => request<{ nonce: string; message: string }>("POST", "/api/gate/nonce"),

  /**
   * Attach a wallet to THIS account.
   *
   * ⚠️ **LINK, NOT SIGN-IN.** The player keeps the account they are already using, along with every
   * deck, star and card on it. Switching identity on connect would hand a guest an empty account and
   * read as the game losing their save.
   */
  linkWallet: (nonce: string, signature: string) =>
    request<{ primaryWalletAddress: string }>("POST", "/api/v1/Identity/LinkWallet",
                                              { nonce, signature }),

  /**
   * Send an emote to the other player.
   *
   * ⚠️ `Id` and the route are the ORIGINAL's, kept exactly - `Game/PlayEmotion`. The server takes the
   * sender from the token, never from the body, so nobody can make another player appear to speak.
   */
  playEmotion: (id: string) =>
    request<{ ok: boolean }>("POST", "/api/v1/Game/PlayEmotion", { Id: id }),

  // ⭐ THE IN-GAME BUG REPORTER. Rebuilt 2026-08-17 after the Unity-to-web migration silently
  // dropped it: the button, the bundle code and the server route had all gone, while `betamark.ts`
  // still told testers reporting a bug was the most useful thing they could do. The snapshot is
  // collected by `app/diagnostics.ts` when the panel OPENS, not when it is submitted.
  reportBug: (r: { message: string; contact: string; snapshot: unknown }) =>
    request<{ ok: boolean; id: string }>("POST", "/api/v1/Bug/Report", r),

  awards: () => request<{ awards: Array<{ reason: string; cards: number; experience: number;
                                          scrip: number; cardIds: string[]; atUtc: string }> }>(
    "GET", "/api/v1/Progression/Awards"),

  // ⭐ THE SECOND CAMPAIGN MODE. Both routes were served and called by NOTHING - see
  // screens/heroCampaign.ts and tools/verify_route_coverage.py.
  //
  // ⚠️ NO deckId on StartStage, deliberately: the original's own request shape carries none
  // (`HeroCampaignStartStageModel { CampaignId, Stage }`) because the hero's deck is fixed, which is
  // what makes the difficulty identical for every player.
  // ⭐ THE TUTORIAL. 53 authored steps, served since the server was built and called by nothing.
  tutorialConfig: () => request<unknown[]>("GET", "/api/v1/Tutorial/GetConfig"),
  tutorialProgress: () => request<Record<string, unknown>>("GET", "/api/v1/Tutorial/GetProgress"),
  tutorialPostProgress: (o: { lastStepId: string; completed: boolean }) =>
    request<Record<string, unknown>>("POST", "/api/v1/Tutorial/PostProgress", o),

  heroCampaignProgress: () =>
    request<Record<string, unknown>>("GET", "/api/v1/HeroCampaign/Progress"),
  heroCampaignStartStage: (o: { campaignId: string; heroId: string; stageIndex: number }) =>
    request<Record<string, unknown>>("POST", "/api/v1/HeroCampaign/StartStage", o),
  /** Claim a campaign milestone's reward. The server refuses with a reason - show it. */
  claimMilestone: (milestoneId: string) =>
    request<Record<string, unknown>>("POST", "/api/v1/Campaign/ClaimMilestone", { milestoneId }),

  gauntletProgress: () => request<Record<string, unknown>>("GET", "/api/v1/Gauntlet/Progress"),
  gauntletStartLevel: (level: number) =>
    request<Record<string, unknown>>("POST", "/api/v1/Gauntlet/StartLevel", { level }),

  duelRooms: () => list<{ id: string; name?: string }>("/api/v1/Lobby/DuelAvailableRooms"),
  duelCreateRoom: () => request<Record<string, unknown>>("POST", "/api/v1/Lobby/DuelCreateRoom", {}),
  duelJoinRoom: (roomId: string) =>
    request<Record<string, unknown>>("POST", "/api/v1/Lobby/DuelJoinRoom", { roomId }),
  /** The room this player is currently in, or `null`. `null` is the honest "none", not an error. */
  duelActiveRoom: () => request<Record<string, unknown> | null>("GET", "/api/v1/Lobby/DuelActiveRoom"),
  duelLeaveRoom: () => request<unknown>("POST", "/api/v1/Lobby/DuelLeaveRoom", {}),
  /**
   * Start the duel. HOST ONLY, and the server enforces it - `DuelRegistry.Start(user, PlayerLimit())`
   * returns an error string rather than throwing, so the caller must SHOW that string.
   */
  duelStartSession: () => request<unknown>("POST", "/api/v1/Lobby/DuelStartSession", {}),

  /**
   * Remove the guest from the room you host.
   *
   * ⚠️ **GET, AND IT TAKES NO ARGUMENTS.** Read off the server, not guessed: the handler derives
   * the room from the CALLER and kicks whoever else is in it (`Program.cs`, `duels.Kick(user)`).
   * There is no player id to pass, and it refuses with `"You are not hosting a room."` if you are
   * not the host, so the button is a host-only affordance rather than a client-side rule.
   */
  duelKickPlayer: () => request<unknown>("GET", "/api/v1/Lobby/DuelKickPlayer"),

  /**
   * QUICK MATCH — ask for a human, and be told honestly when there is not one.
   *
   * ⭐ **ONE IDEMPOTENT ENDPOINT, POLLED.** Calling it again does not enqueue a second copy of you and
   * does not restart your wait clock (the server keeps two timestamps for exactly that reason). Three
   * answers, and the caller must handle all three:
   *
   *   `matched` — a shared session already exists; go to the board and it will JOIN, not start
   *   `waiting` — keep polling; `fallbackInMs` is how long until a bot is offered
   *   `bot`     — nobody came, you are out of the queue; start a solo match AND SAY SO
   *
   * ⛔ **`bot` IS AN ANSWER, NOT SOMETHING THE SERVER DID FOR YOU.** Never navigate into an AI game
   * without telling the player. A bot arriving unannounced is the exact defect the duel fix exists to
   * correct, and it cost a real player a match against a friend.
   */
  quickMatch: (deckId?: string, leagueId?: string) =>
    request<{
      status: "matched" | "waiting" | "bot";
      matchId?: string;
      opponent?: string;
      pvp?: boolean;
      waitedMs?: number;
      fallbackInMs?: number;
      queued?: number;
    }>("POST", "/api/v1/Lobby/QuickMatch", { deckId, leagueId }),

  /** Release the slot. A queue entry nobody is behind pairs the next real player with a ghost. */
  quickMatchCancel: () => request<{ left?: boolean }>("POST", "/api/v1/Lobby/QuickMatchCancel", {}),
  /**
   * Start a solo match.
   *
   * 🚨 **THE `deckId` IS NOT OPTIONAL IN PRACTICE, ONLY IN THE SIGNATURE.** This used to post `{}`,
   * and `MatchRegistry.cs:215` falls back to `_data.Decks[0]` - so every match this client ever
   * started used the same deck regardless of what the player thought they had chosen. The route
   * accepted a deck the whole time; nobody sent one.
   */
  // ⚠️ `difficulty` is OPTIONAL and omitted means Normal - never an accidental Hard. The engine has
  // shipped Normal and Hard the whole time and nothing ever offered either.
  /** Every authored puzzle, served from the campaign template - see /api/v1/Puzzle/List. */
  puzzleList: () => request<{ puzzles: Array<{ quadrant: string; stageIndex: number;
                                               isHeroic: boolean; title: string;
                                               objective: string; objectiveText: string }> }>(
    "GET", "/api/v1/Puzzle/List"),

  practiceStart: (deckId?: string, difficulty?: string, specialRule?: string) =>
    request<Record<string, unknown>>("POST", "/api/v1/Lobby/PracticeStartSession", {
      ...(deckId ? { deckId } : {}),
      ...(difficulty ? { difficulty } : {}),
      // ⚠️ The server REFUSES a rule it does not implement rather than carrying it into a match that
      // would play as an ordinary game - see MatchSession.SpecialRules.
      ...(specialRule ? { specialRule } : {}),
    }),

  /**
   * ⭐ PLAYER SETTINGS, PER PROFILE. Volumes follow the player, not the browser.
   *
   * ⚠️ The server clamps to 0..1 as well as the UI: a volume outside that range throws
   * IndexSizeError on an HTMLAudioElement, so a bad stored value would break audio permanently for
   * that player on every future load.
   */
  /**
   * A raw authenticated GET, for TOOLING only.
   *
   * ⚠️ Exists because a harness `fetch(..., {credentials:'include'})` carries the cookie but not
   * the bearer token, so it reads 401 and reports "the server offers none" about content that is
   * there. Never used by a screen: a screen should call a NAMED method, so the routes it depends on
   * are visible in one file.
   */
  raw: (path: string) => request<unknown>("GET", path),

  settings: () => request<Record<string, unknown>>("GET", "/api/v1/Revival/Settings"),
  saveSettings: (sfxVolume: number, musicVolume: number) =>
    request<Record<string, unknown>>("POST", "/api/v1/Revival/Settings", { sfxVolume, musicVolume }),

  /** ⭐ OUR OWN MECHANIC. Not the previous studio's - see `Foxfire.cs` on the server. */
  foxfire: () => request<Record<string, unknown>>("GET", "/api/v1/Revival/Foxfire"),
  /**
   * Burn a card from hand for its typed Foxfire token.
   *
   * ⚠️ **THE FIELD IS `objectId`.** The route reads `dto["objectId"] ?? dto["ObjectId"]` and answers
   * `400 objectId is required` for anything else - which is what a first attempt sending
   * `cardObjectId` got: the card stayed in hand, the pool stayed empty, and the only witness was the
   * server's access log. Names on the wire are a contract, not a preference.
   */
  foxfireSacrifice: (objectId: number) =>
    request<Record<string, unknown>>("POST", "/api/v1/Revival/Foxfire/Sacrifice", { objectId }),

  /** The ladder. OURS - the original never had this route, so it carries no legacy shape. */
  standings: () => list<Record<string, unknown>>("/api/v1/Revival/Standings"),

  playerStatistics: () => request<Record<string, unknown>>("GET", "/api/v1/Player/Statistics"),
};
